import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";

function Stars({ rating, size = 14, color = "#111" }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} width={size} height={size} viewBox="0 0 20 20" fill={n <= rating ? color : "none"} stroke={color} strokeWidth="1.4">
          <path d="M10 1.5l2.63 5.32 5.87.85-4.25 4.14 1 5.85L10 14.9l-5.25 2.76 1-5.85L1.5 7.67l5.87-.85L10 1.5z" strokeLinejoin="round" />
        </svg>
      ))}
    </span>
  );
}

export function StarRating({ rating, count, accent = "#111" }) {
  if (!count) return null;
  return (
    <span data-testid="rating-summary" className="inline-flex items-center gap-2">
      <Stars rating={Math.round(rating)} color={accent} />
      <span className="font-mono-label text-[10px] text-neutral-600">
        {rating.toFixed(1)} · {count} review{count === 1 ? "" : "s"}
      </span>
    </span>
  );
}

export default function ReviewsSection({ productSlug, productName, accent = "#111", reviews, avg, count, onReviewAdded }) {
  const { lang } = useI18n();
  const nl = lang === "nl";
  const de = lang === "de";
  const L = {
    title: nl ? "Wat onderzoekers zeggen" : de ? "Was Forscher sagen" : "What researchers say",
    basedOn: (n) => nl ? `Op basis van ${n} reviews` : de ? `Basierend auf ${n} Bewertungen` : `Based on ${n} reviews`,
    write: nl ? "Schrijf review" : de ? "Bewertung schreiben" : "Write review",
    close: nl ? "Sluiten" : de ? "Schließen" : "Close",
    newReview: nl ? "NIEUWE REVIEW" : de ? "NEUE BEWERTUNG" : "NEW REVIEW",
    rating: nl ? "Beoordeling:" : de ? "Bewertung:" : "Rating:",
    name: nl ? "Naam" : de ? "Name" : "Name",
    fieldTitle: nl ? "Titel" : de ? "Titel" : "Title",
    experience: nl ? "Je ervaring…" : de ? "Ihre Erfahrung…" : "Your experience…",
    submit: nl ? "Plaats review →" : de ? "Bewertung abschicken →" : "Post review →",
    thanks: nl ? "Review geplaatst — bedankt!" : de ? "Bewertung veröffentlicht — danke!" : "Review posted — thanks!",
    none: nl ? "Nog geen reviews. Wees de eerste!" : de ? "Noch keine Bewertungen. Seien Sie der Erste!" : "No reviews yet. Be the first!",
    fillAll: nl ? "Vul alle velden in" : de ? "Alle Felder ausfüllen" : "Fill all fields",
    verified: nl ? "✓ Geverifieerde aankoop" : de ? "✓ Verifizierter Kauf" : "✓ Verified purchase",
    failed: nl ? "Mislukt" : de ? "Fehlgeschlagen" : "Failed",
  };
  const [form, setForm] = useState({ name: "", title: "", body: "", rating: 5 });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.title || !form.body) {
      toast.error(L.fillAll);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_slug: productSlug, ...form });
      toast.success(L.thanks);
      setForm({ name: "", title: "", body: "", rating: 5 });
      setShowForm(false);
      if (onReviewAdded) onReviewAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || L.failed);
    } finally { setSubmitting(false); }
  };

  return (
    <section data-testid="reviews-section" className="mt-24 border-t border-neutral-300 pt-16">
      <div className="grid md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-4">
          <p className="font-mono-label text-[11px] text-neutral-500 mb-3">[ REVIEWS ]</p>
          <h2 className="font-serif-display font-light text-4xl md:text-5xl leading-[1.02]">
            {L.title}
          </h2>
          {count > 0 && (
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-serif-display text-6xl" style={{ color: accent }}>{avg.toFixed(1)}</span>
              <div>
                <Stars rating={Math.round(avg)} size={16} color={accent} />
                <p className="font-mono-label text-[10px] text-neutral-500 mt-1">{L.basedOn(count)}</p>
              </div>
            </div>
          )}
          <button
            data-testid="write-review"
            onClick={() => setShowForm((s) => !s)}
            className="btn-sharp mt-8"
          >
            {showForm ? L.close : L.write}
          </button>
        </div>

        <div className="md:col-span-8">
          {showForm && (
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-neutral-300 bg-white p-6 mb-8 space-y-3"
            >
              <p className="font-mono-label text-[10px] text-neutral-500">[ {L.newReview} · {productName.toUpperCase()} ]</p>
              <div className="flex items-center gap-2">
                <span className="font-mono-label text-[10px] text-neutral-500">{L.rating}</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-1"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill={n <= form.rating ? "#111" : "none"} stroke="#111" strokeWidth="1.4">
                      <path d="M10 1.5l2.63 5.32 5.87.85-4.25 4.14 1 5.85L10 14.9l-5.25 2.76 1-5.85L1.5 7.67l5.87-.85L10 1.5z" strokeLinejoin="round" />
                    </svg>
                  </button>
                ))}
              </div>
              <input placeholder={L.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-neutral-300 px-3 py-2" />
              <input placeholder={L.fieldTitle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-neutral-300 px-3 py-2" />
              <textarea placeholder={L.experience} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 min-h-24" />
              <button data-testid="submit-review" type="submit" disabled={submitting} className="btn-solid">
                {submitting ? "…" : L.submit}
              </button>
            </motion.form>
          )}

          <div className="space-y-8">
            {reviews.map((r) => (
              <motion.div
                key={r.id}
                data-testid={`review-${r.id}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5 }}
                className="border-b border-neutral-200 pb-8"
              >
                <div className="flex items-baseline justify-between mb-2">
                  <Stars rating={r.rating} color={accent} />
                  <span className="font-mono-label text-[9px] text-neutral-400">
                    {new Date(r.created_at).toLocaleDateString(nl ? "nl-NL" : de ? "de-DE" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </div>
                <h4 className="font-serif-display text-2xl leading-tight">{r.title}</h4>
                <p className="mt-3 text-neutral-600 leading-relaxed">{r.body}</p>
                <p className="mt-3 font-mono-label text-[10px] text-neutral-500">
                  — {r.name} <span className="text-emerald-700 ml-1">{L.verified}</span>
                </p>
              </motion.div>
            ))}
            {reviews.length === 0 && (
              <p className="text-neutral-500 text-sm">{L.none}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
